import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Button,
  Color,
  Display,
  Display2,
  FontWeight,
  Heading3,
  Space,
  Visible,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { CWC_HOW_IT_WORKS, HOW_OUR_RECIPE_BOXES_WORK } from '../../../config'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../../tracking'

type BenefitsProps = {
  signupCTA: string
  onCTAClick: () => void
  enableNewHowItWorksCopy?: boolean
}

const { header, benefits } = HOW_OUR_RECIPE_BOXES_WORK

const OutlinedLinkButton = withCitrusTheme('a', (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '327px',
  padding: '12px 16px',
  border: `2px solid ${theme.color(Color.Secondary_400)}`,
  borderRadius: '8px',
  color: theme.color(Color.Secondary_400),
  fontFamily: 'inherit',
  fontWeight: FontWeight.Bold,
  fontSize: '18px',
  lineHeight: '28px',
  textDecoration: 'none',
  boxSizing: 'border-box' as const,
  '&:hover': {
    color: theme.color(Color.Secondary_700),
    borderColor: theme.color(Color.Secondary_700),
  },
  '&:focus': {
    outline: `3px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
}))

export function Benefits({ signupCTA, onCTAClick, enableNewHowItWorksCopy }: BenefitsProps) {
  const clickCTA = () => {
    onCTAClick()
    trackCTAClick(
      TrackingActions.CLICK_GET_STARTED,
      LandingPageContentSections.EFFORTLESS_HOME_COOKING,
    )
  }

  const activeCopy = enableNewHowItWorksCopy ? CWC_HOW_IT_WORKS : { header, benefits }

  const benefitList = activeCopy.benefits.map(({ title, description }, index) => (
    <Box key={`benefit-${index}`}>
      <Heading3
        size={[4, null, null, 6]}
        color={Color.ColdGrey_800}
        style={{ fontWeight: FontWeight.Bold }}
      >
        {index + 1}. {title}
      </Heading3>
      <Body2 size={[4, null, null, 6]} color={Color.ColdGrey_800}>
        {description}
      </Body2>
      <Space size={3} />
    </Box>
  ))

  return (
    <Box display={Display.Flex} alignItems={AlignItems.Center} width={[null, null, null, '100%']}>
      <Box>
        <Box paddingRight={0} width={[null, null, null, '350px']}>
          <Display2 size={[11, 13]} color={Color.Primary_600}>
            {activeCopy.header}
          </Display2>
        </Box>
        <Space size={[4, null, null, 5]} />
        <Box maxWidth={[null, null, null, '380px']}>{benefitList}</Box>
        <Space size={3} />
        {enableNewHowItWorksCopy ? (
          <OutlinedLinkButton href={CWC_HOW_IT_WORKS.linkButtonUrl}>
            {CWC_HOW_IT_WORKS.linkButtonLabel}
          </OutlinedLinkButton>
        ) : (
          <Visible display={[Display.None, Display.Block, null, Display.Block]}>
            <Button width="320px" onClick={clickCTA} height="48px">
              {signupCTA}
            </Button>
            <Space size={[0, 3, 3, 0]} />
          </Visible>
        )}
      </Box>
    </Box>
  )
}
